"use client";

/**
 * 录制自定义音色。
 *
 * 三步，缺一不可：
 *   1. 录一段 3~30 秒的话
 *   2. ASR 转写 → **让用户逐字校对**
 *   3. 提交音频 + 校对后的文本
 *
 * 第 2 步是整个流程里最容易被省掉、也最不能省的一步。CosyVoice 做零样本克隆
 * 时要拿 prompt_text 和音频对齐，文本和录音对不上，克隆出来的音色就会崩
 * （表现为口音怪、吞字、语调飘）。ASR 会认错字，所以必须让人改。
 */

import { useEffect, useRef, useState } from "react";
import { Loader2, Mic, Square, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ensureFreshAccessToken } from "@/lib/api";
import { VoiceRecorder, blobToAsrBase64 } from "@/lib/voice-recorder";
import { blobToWav, type WavResult } from "@/lib/wav";
import { voicesApi, type ServerVoice } from "@/lib/voices-api";
import { cn } from "@/lib/utils";

/** 与后端 MIN_AUDIO_SECONDS / MAX_AUDIO_SECONDS 保持一致。
 *  在这里也拦一道，是为了让用户在录完当场就知道，而不是提交后等一趟往返。 */
const MIN_SECONDS = 3;
const MAX_SECONDS = 30;

/** 建议朗读的示例句。用日常口语、覆盖常见声母韵母，不带产品名。 */
const SAMPLE_SENTENCES = [
  "今天天气不错，我打算下午去公园走一走，顺便买点水果回来。",
  "这件事我想了很久，还是觉得应该先把手头的工作做完再说。",
  "麻烦你帮我看一下这份材料，有什么问题随时告诉我。"
];

type Step = "guide" | "recording" | "confirm";

export function VoiceRecorderDialog({
  open,
  onOpenChange,
  onCreated
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (v: ServerVoice) => void;
}) {
  const [step, setStep] = useState<Step>("guide");
  const [level, setLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [wav, setWav] = useState<WavResult | null>(null);
  const [transcribing, setTranscribing] = useState(false);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [sample] = useState(() => SAMPLE_SENTENCES[Math.floor(Math.random() * SAMPLE_SENTENCES.length)]);

  const recorderRef = useRef<VoiceRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wavRef = useRef<WavResult | null>(null);

  // 关闭时彻底复位：录音是占着麦克风的资源，留着不放会一直亮着录音指示灯
  useEffect(() => {
    if (open) return;
    void recorderRef.current?.cancel();
    recorderRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
    if (wavRef.current) URL.revokeObjectURL(wavRef.current.url);
    wavRef.current = null;
    setStep("guide");
    setLevel(0);
    setElapsed(0);
    setWav(null);
    setText("");
    setName("");
    setSaving(false);
    setTranscribing(false);
  }, [open]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (wavRef.current) URL.revokeObjectURL(wavRef.current.url);
    };
  }, []);

  async function startRecording() {
    const rec = new VoiceRecorder({
      // 克隆必须要原始音频：降噪和自动增益会改变音色本身
      rawAudio: true,
      // 静音不自动停：朗读一句话中间本来就有停顿，VAD 掐掉会把句子截断。
      // 用超时兜底就够了。
      silenceMs: 60_000,
      maxMs: MAX_SECONDS * 1000,
      onLevel: setLevel
    });
    recorderRef.current = rec;
    try {
      await rec.start();
    } catch (e) {
      const code = e instanceof Error ? e.message : "";
      toast.error(
        code === "permission_denied"
          ? "麦克风被拒绝了，请在浏览器地址栏的权限里允许后重试"
          : code === "no_mic"
            ? "没有找到麦克风"
            : "这个浏览器不支持录音，换 Chrome 或 Safari 试试"
      );
      recorderRef.current = null;
      return;
    }
    setStep("recording");
    setElapsed(0);
    const startedAt = Date.now();
    timerRef.current = setInterval(() => {
      const s = (Date.now() - startedAt) / 1000;
      setElapsed(s);
      if (s >= MAX_SECONDS) void stopRecording();
    }, 100);
  }

  async function stopRecording() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const rec = recorderRef.current;
    if (!rec) return;
    const raw = await rec.stop();
    recorderRef.current = null;
    setLevel(0);
    if (!raw) {
      toast.error("没有录到声音，请检查麦克风后重试");
      setStep("guide");
      return;
    }

    let converted: WavResult;
    try {
      converted = await blobToWav(raw);
    } catch {
      toast.error("录音处理失败，请重新录一次");
      setStep("guide");
      return;
    }

    if (converted.seconds < MIN_SECONDS) {
      URL.revokeObjectURL(converted.url);
      toast.error(`只录到 ${converted.seconds.toFixed(1)} 秒，请说满 ${MIN_SECONDS} 秒以上`);
      setStep("guide");
      return;
    }

    if (wavRef.current) URL.revokeObjectURL(wavRef.current.url);
    wavRef.current = converted;
    setWav(converted);
    setStep("confirm");
    void transcribe(raw);
  }

  /** ASR 只是给用户一个起点，不是最终答案——所以失败也不挡流程，让他自己敲。 */
  async function transcribe(raw: Blob) {
    setTranscribing(true);
    try {
      const token = await ensureFreshAccessToken();
      if (!token) throw new Error("no_token");
      const res = await fetch("/api/asr", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ audio: await blobToAsrBase64(raw) })
      });
      if (!res.ok) throw new Error("asr_failed");
      const data = (await res.json()) as { text?: string };
      if (data.text) setText(data.text.trim());
      else toast.info("没识别出文字，请手动输入你刚才说的那句话");
    } catch {
      toast.info("自动转写没成功，请手动输入你刚才说的那句话");
    } finally {
      setTranscribing(false);
    }
  }

  async function save() {
    const trimmedName = name.trim();
    const trimmedText = text.trim();
    if (!trimmedName) return toast.error("给这个音色起个名字");
    if (trimmedText.length < 2) return toast.error("请填写你刚才念的那句话");
    if (!wav) return;

    setSaving(true);
    try {
      const created = await voicesApi.create(trimmedName, trimmedText, wav.base64);
      toast.success(`「${created.name}」已创建`);
      onCreated(created);
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "创建失败，请稍后再试");
    } finally {
      setSaving(false);
    }
  }

  function retake() {
    if (wavRef.current) URL.revokeObjectURL(wavRef.current.url);
    wavRef.current = null;
    setWav(null);
    setText("");
    setStep("guide");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>录制我的音色</DialogTitle>
          <DialogDescription>
            录一段你自己的声音，之后朗读就用这个音色。录音只有你能听到和下载。
          </DialogDescription>
        </DialogHeader>

        {step === "guide" && (
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-secondary/40 p-4">
              <p className="mb-2 text-sm font-medium">录之前先看一眼</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li>· 找个安静的房间，关掉音乐和电视，避开空调出风口</li>
                <li>· 别在空旷的大房间里录，回声会让克隆出来的声音发闷</li>
                <li>· 嘴离麦克风 15~20 厘米，太近会有喷麦的噗噗声</li>
                <li>· 用平时说话的语速和语气，别刻意播音腔——录得夸张，合成出来也夸张</li>
                <li>· 一次说完一句完整的话，{MIN_SECONDS}~{MAX_SECONDS} 秒</li>
              </ul>
            </div>

            <p className="text-xs text-muted-foreground">
              录完会自动转写成文字，<strong className="text-foreground">请务必逐字核对一遍</strong>
              ——文字和录音对不上，克隆质量会明显变差。
            </p>

            <Button onClick={startRecording} className="w-full gap-2">
              <Mic className="h-4 w-4" />
              开始录音
            </Button>
          </div>
        )}

        {step === "recording" && (
          <div className="space-y-6 py-4">
            <div className="flex flex-col items-center gap-4">
              <div
                className="flex h-24 w-24 items-center justify-center rounded-full bg-accent/10 transition-transform"
                style={{ transform: `scale(${1 + Math.min(level, 1) * 0.25})` }}
              >
                <Mic className="h-8 w-8 text-accent" />
              </div>
              <p className="text-2xl tabular-nums">{elapsed.toFixed(1)}s</p>
              <p className="text-sm text-muted-foreground">
                {elapsed < MIN_SECONDS
                  ? `再说 ${(MIN_SECONDS - elapsed).toFixed(1)} 秒就够了`
                  : "够了，说完这句就可以停"}
              </p>
            </div>
            <Button onClick={stopRecording} variant="secondary" className="w-full gap-2">
              <Square className="h-4 w-4" />
              停止
            </Button>

            {/* 示例句放在录音**过程中**，不放在录之前那一屏：录之前看一眼是记不住的，
                真正需要它的时刻是麦克风已经开着、正要开口。 */}
            <div className="rounded-xl border border-border p-4">
              <p className="mb-2 text-sm text-muted-foreground">可以念这句（也可以说你自己的话）</p>
              <p className="text-base leading-relaxed">{sample}</p>
            </div>
          </div>
        )}

        {step === "confirm" && wav && (
          <div className="space-y-4">
            <div className="rounded-xl border border-border p-3">
              <p className="mb-2 text-xs text-muted-foreground">
                录了 {wav.seconds.toFixed(1)} 秒 —— 先听一遍，觉得不对就重录
              </p>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio src={wav.url} controls className="w-full" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="voice-text">
                你刚才说的那句话
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  （必须逐字一致，错字会让音色变差）
                </span>
              </Label>
              <div className="relative">
                <textarea
                  id="voice-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={3}
                  disabled={transcribing}
                  placeholder={transcribing ? "" : "请输入你刚才念的那句话"}
                  className={cn(
                    "w-full resize-none rounded-xl border border-input bg-transparent px-3 py-2 text-sm",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                    "disabled:opacity-60"
                  )}
                />
                {transcribing && (
                  <span className="absolute left-3 top-2 inline-flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    正在转写…
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="voice-name">音色名字</Label>
              <Input
                id="voice-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={32}
                placeholder="比如：我的声音"
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={retake} className="gap-2" disabled={saving}>
                <RotateCcw className="h-4 w-4" />
                重录
              </Button>
              <Button onClick={save} disabled={saving || transcribing} className="flex-1 gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {saving ? "创建中…" : "确认创建"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
